import "@/App.css";
import { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";

import HomePage from "@/pages/HomePage";
import PlatformPage from "@/pages/PlatformPage";
import PricingPage from "@/pages/PricingPage";
import SuccessPage from "@/pages/SuccessPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import AdminPage from "@/pages/AdminPage";

import SignupPage from "@/pages/auth/SignupPage";
import LoginPage from "@/pages/auth/LoginPage";
import AuthCallback from "@/pages/auth/AuthCallback";
import DashboardHome from "@/pages/dashboard/DashboardHome";
import VaultPage from "@/pages/dashboard/VaultPage";
import StudioBuilderPage from "@/pages/dashboard/StudioBuilderPage";
import ProjectDetailPage from "@/pages/dashboard/ProjectDetailPage";
import MonetizeHome from "@/pages/dashboard/MonetizeHome";
import MonetizeStorefront from "@/pages/dashboard/MonetizeStorefront";
import MonetizeMarketingPage from "@/pages/MonetizeMarketingPage";
import PublicStorefront from "@/pages/PublicStorefront";
import OnboardingWizard from "@/pages/onboarding/OnboardingWizard";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import { AuthProvider } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";

const PLAUSIBLE_DOMAIN = process.env.REACT_APP_PLAUSIBLE_DOMAIN;

function PlausibleLoader() {
  useEffect(() => {
    if (!PLAUSIBLE_DOMAIN) return;
    if (document.querySelector('script[data-plausible="true"]')) return;
    window.plausible =
      window.plausible ||
      function () {
        (window.plausible.q = window.plausible.q || []).push(arguments);
      };
    const s = document.createElement("script");
    s.defer = true;
    s.dataset.domain = PLAUSIBLE_DOMAIN;
    s.dataset.plausible = "true";
    s.src = "https://plausible.io/js/script.tagged-events.js";
    document.head.appendChild(s);
    // PLAUSIBLE_DOMAIN is a module-level env constant - runs once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    if (typeof window !== "undefined" && typeof window.plausible === "function") {
      window.plausible("pageview");
    }
  }, [location.pathname]);
  return null;
}

function ScrollToTop() {
  if (typeof window !== "undefined") {
    window.scrollTo({ top: 0, behavior: "instant" });
  }
  return null;
}

function MarketingLayout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-white text-neutral-900">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

const wrap = (Page) => (
  <MarketingLayout>
    <ScrollToTop />
    <Page />
  </MarketingLayout>
);

/**
 * Synchronously detect Emergent Google OAuth return (#session_id=...)
 * BEFORE normal route resolution. This prevents AuthProvider's /me check
 * from racing the session cookie write.
 */
function AppRouter() {
  const location = useLocation();
  if (typeof window !== "undefined" && location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={wrap(HomePage)} />
      <Route path="/platform" element={wrap(PlatformPage)} />
      <Route path="/pricing" element={wrap(PricingPage)} />
      <Route path="/success" element={wrap(SuccessPage)} />
      <Route path="/about" element={wrap(AboutPage)} />
      <Route path="/contact" element={wrap(ContactPage)} />

      {/* Auth */}
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/login" element={<LoginPage />} />

      {/* Protected app */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute requireOnboarding={false}>
            <OnboardingWizard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardHome />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/vault"
        element={
          <ProtectedRoute requireOnboarding={false}>
            <VaultPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/builder"
        element={
          <ProtectedRoute requireOnboarding={false}>
            <StudioBuilderPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/vault/:id"
        element={
          <ProtectedRoute requireOnboarding={false}>
            <ProjectDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/monetize"
        element={
          <ProtectedRoute requireOnboarding={false}>
            <MonetizeHome />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/monetize/store/:id"
        element={
          <ProtectedRoute requireOnboarding={false}>
            <MonetizeStorefront />
          </ProtectedRoute>
        }
      />
      <Route path="/store/:slug" element={<PublicStorefront />} />
      <Route path="/monetize" element={<MonetizeMarketingPage />} />

      {/* Legacy redirects */}
      <Route path="/services" element={<Navigate to="/platform" replace />} />
      <Route path="/portfolio" element={<Navigate to="/success" replace />} />

      <Route path="/admin" element={<AdminPage />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <PlausibleLoader />
      <BrowserRouter>
        <AuthProvider>
          <RouteTracker />
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}
